import { Component, ViewChild, ElementRef, input, output, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../../../../../modules/catch-core/third-party/material/material.module';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { DataSource } from '@angular/cdk/collections';
import { BehaviorSubject, merge, Observable } from 'rxjs';
import { DataService } from '../services/data.service';
import { AddDialogComponent } from '../dialog/add-dialog/add-dialog.component';
import { EditDialogComponent } from '../dialog/edit-dialog/edit-dialog.component';
import { DeleteDialogComponent } from '../dialog/delete-dialog/delete-dialog.component';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-blueprint-property-binding-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.css'],
  standalone: true,
  imports: [CommonModule, MaterialModule],
})
export class TableComponent {
  // Using Angular 20 signal-based inputs and outputs
  inputTableData = input<any[]>([]);
  inputTableConfig = input<any[]>([]);
  isReadOnly = input<boolean>(false);
  outputTableDataEvent = output<any>();

  public displayColumnsWithCatchIndex: {}[] = [];
  public displayColumnsWithOutCatchIndex: string[] = [];
  public dataSource: TableDataSource | null = null;
  private propertyRecordCount = 0;

  public isNewRecordAddAvailalbe = true;

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;
  @ViewChild('filter') filter!: ElementRef;

  constructor(
    public dialog: MatDialog,
    private dataService: DataService,
  ) {
    // Using effect to react to input changes (replaces ngOnChanges)
    effect(() => {
      const tableData = this.inputTableData();
      const tableConfig = this.inputTableConfig();

      if (tableData || tableConfig) {
        this.setupDataService();
        this.loadData();
      }
    });
  }

  public refresh() {
    this.loadData();
  }

  public addRecord() {
    if (this.isReadOnly()) {
      return;
    }
    const dialogRef = this.dialog.open(AddDialogComponent, {
      data: {},
      width: '25%',
      height: '50%',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 1) {
        this.dataService.dataChange.value.push(this.dataService.getDialogData());
        this.refreshTable();
        this.propertyRecordCount++;
        this.limitTo3Records();
        this.outputTableDataEvent.emit(this.dataService.getAllCurrentData());
      }
    });
  }

  public editRecord(dataRow: any) {
    if (this.isReadOnly()) {
      return;
    }
    const dialogRef = this.dialog.open(EditDialogComponent, {
      data: { data: dataRow },
      width: '25%',
      height: '50%',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 1) {
        const foundIndex = this.dataService.dataChange.value.findIndex((x) => x.catchindex === dataRow.catchindex);
        this.dataService.dataChange.value[foundIndex] = this.dataService.getDialogData();
        this.refreshTable();
        this.outputTableDataEvent.emit(this.dataService.getAllCurrentData());
      }
    });
  }

  public deleteRecord(dataRow: any) {
    if (this.isReadOnly()) {
      return;
    }
    const dialogRef = this.dialog.open(DeleteDialogComponent, {
      data: { data: dataRow },
      width: '25%',
      height: '50%',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 1) {
        const foundIndex = this.dataService.dataChange.value.findIndex((x) => x.catchindex === dataRow.catchindex);
        this.dataService.dataChange.value.splice(foundIndex, 1);
        this.propertyRecordCount--;
        this.limitTo3Records();
        this.refreshTable();
        this.outputTableDataEvent.emit(this.dataService.getAllCurrentData());
      }
    });
  }

  private refreshTable() {
    if (this.dataSource) {
      if (this.dataSource._paginator.hasNextPage()) {
        this.dataSource._paginator.nextPage();
        this.dataSource._paginator.previousPage();
      } else if (this.dataSource._paginator.hasPreviousPage()) {
        this.dataSource._paginator.previousPage();
        this.dataSource._paginator.nextPage();
      } else {
        this.dataSource.filter = '';
      }
    }
  }

  public loadData() {
    this.dataSource = new TableDataSource(this.dataService, this.paginator, this.sort);

    this.displayColumnsWithOutCatchIndex = this.dataService.getColumnNamesWithoutRowId();
    this.displayColumnsWithOutCatchIndex.push('actions');
    this.displayColumnsWithCatchIndex = this.dataService.getColumnHeadersWithNames();
  }

  private limitTo3Records() {
    this.isNewRecordAddAvailalbe = this.propertyRecordCount < 3;
  }

  private setupDataService() {
    this.propertyRecordCount = this.inputTableData().length;
    this.limitTo3Records();
    this.dataService.initData(this.inputTableConfig(), this.inputTableData());
  }
}

export class TableDataSource extends DataSource<any> {
  _filterChange = new BehaviorSubject('');

  get filter(): string {
    return this._filterChange.value;
  }

  set filter(filter: string) {
    this._filterChange.next(filter);
  }

  filteredData: any[] = [];
  renderedData: any[] = [];

  constructor(
    public _propertyBindingDatabase: DataService,
    public _paginator: MatPaginator,
    public _sort: MatSort,
  ) {
    super();
    this._filterChange.subscribe(() => (this._paginator.pageIndex = 0));
  }

  connect(): Observable<any[]> {
    const displayDataChanges = [this._propertyBindingDatabase.dataChange, this._sort.sortChange, this._filterChange, this._paginator.page];

    return merge(...displayDataChanges).pipe(
      map(() => {
        this.filteredData = this._propertyBindingDatabase.getAllCurrentData().slice();
        const sortedData = this.sortData(this.filteredData.slice());
        const startIndex = this._paginator.pageIndex * this._paginator.pageSize;
        this.renderedData = sortedData.splice(startIndex, this._paginator.pageSize);
        return this.renderedData;
      }),
    );
  }

  disconnect() {}

  sortData(data: any[]): any[] {
    return data;
  }
}
